import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { authenticatePasskey } from '../utils/passkey'; // <--- BİYOMETRİK FONKSİYON IMPORT EDİLDİ
import {
  isLoginAllowedByTimeRestriction,
  LOGIN_TIME_RESTRICTION_DENIED_MESSAGE
} from '../utils/loginTimeRestriction';
import { verifyPersonelLoginApi } from '../utils/personelAuthClient';

import { 
  Avatar, Typography, Paper, Grid, TextField, Button, 
  IconButton, CircularProgress, Box, Container 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import FingerprintIcon from '@mui/icons-material/Fingerprint';

function SubeGirisi() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // URL'den hangi şube olduğunu alıyoruz (Örn: ?sube=sefakoy_merkez)
  const queryParams = new URLSearchParams(location.search);
  const subeKey = queryParams.get('sube');

  // Başlıkları Düzgün Göstermek İçin Sözlük
  const subeIsimleri = {
    sefakoy_merkez: 'Sefaköy Merkez Mağaza',
    sefakoy_sube: 'Sefaköy Şube Mağaza',
    kavakli_sube: 'Kavaklı Şube Mağaza'
  };
  const currentSubeName = subeIsimleri[subeKey] || 'Şube Girişi';

  // State'ler (Hafıza)
  const [personelList, setPersonelList] = useState([]); // Personel listesi
  const [selectedPersonel, setSelectedPersonel] = useState(null); // Seçilen kişi
  const [password, setPassword] = useState(''); // Girilen şifre
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false); // Giriş işlemi loading
  const [pageLoading, setPageLoading] = useState(true); // Sayfa ilk açılış loading

  const buildSafeSessionPersonel = (personel) => ({
    id: personel?.id,
    isim: personel?.isim,
    soyisim: personel?.soyisim,
    rol: personel?.rol || 'personel',
    aktif: personel?.aktif !== false,
    yetkiler: personel?.yetkiler || { subeler: [], moduller: [], ozelIzinler: {} }
  });

  const assertSelectedPersonelCanCompleteLogin = () => {
    if (selectedPersonel.aktif === false) {
      setError('Bu hesap pasif. Giriş yapılamaz.');
      return false;
    }
    if (!subeKey || !Array.isArray(selectedPersonel.yetkiler?.subeler) || !selectedPersonel.yetkiler.subeler.includes(subeKey)) {
      setError('Bu şube için giriş yetkiniz bulunmuyor.');
      return false;
    }
    if (!isLoginAllowedByTimeRestriction(selectedPersonel.loginTimeRestriction)) {
      setError(LOGIN_TIME_RESTRICTION_DENIED_MESSAGE);
      return false;
    }
    return true;
  };

  // --- 1. SAYFA AÇILINCA: O ŞUBENİN PERSONELLERİNİ GETİR ---
  useEffect(() => {
    const fetchPersonel = async () => {
      if (!subeKey) return;
      try {
        // Veritabanına soruyoruz: "Yetkilerinde bu şube olan personelleri getir"
        const q = query(
          collection(db, 'personeller'),
          where('yetkiler.subeler', 'array-contains', subeKey),
          where('aktif', '==', true) // Sadece aktif çalışanlar
        );
        const snapshot = await getDocs(q);
        /** personelyonetimi ile aynı: dokümandaki id alanı gerçek doc.id'yi ezmesin */
        const list = snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }));
        setPersonelList(list);
      } catch (err) {
        console.error("Personel çekme hatası:", err);
      } finally {
        setPageLoading(false);
      }
    };
    fetchPersonel();
  }, [subeKey]);

  // --- 2. GİRİŞ YAPMA İŞLEMİ (ŞİFRE İLE) ---
  // Şifre karşılaştırması artık istemcide YAPILMAZ; sunucu (Admin SDK) doğrular.
  // selectedPersonel'de sifre alanı zaten hiç bulunmaz (bkz. personelAuthService.js).
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!selectedPersonel) return;

    setLoading(true);
    setError('');

    // Hızlı ön-kontrol (UX): açık bir engel varsa gereksiz ağ isteği atma.
    // Sunucu tarafı, otoritatif olarak aynı kontrolleri zaten tekrar yapıyor.
    if (!assertSelectedPersonelCanCompleteLogin()) {
      setLoading(false);
      return;
    }

    try {
      const result = await verifyPersonelLoginApi({
        subeKey,
        personelId: selectedPersonel.id,
        password
      });

      if (!result.ok) {
        setError(result.message || 'Hatalı şifre!');
        setLoading(false);
        return;
      }

      localStorage.setItem('aktifPersonel', JSON.stringify(buildSafeSessionPersonel(result.personel)));
      navigate(`/dashboard?sube=${subeKey}`);
    } catch (err) {
      console.error('Giriş hatası:', err);
      setError('Giriş sırasında bir hata oluştu. Lütfen tekrar deneyin.');
      setLoading(false);
    }
  };

  // --- 3. BİYOMETRİK GİRİŞ İŞLEMİ (YENİ) ---
  const handleBiometricLogin = async () => {
    setLoading(true);
    try {
      // Tarayıcıya "Yüzünü/Parmağını sor" diyoruz
      const credentialId = await authenticatePasskey(selectedPersonel.passkeys);
      
      // Eğer başarılı dönerse ve bu ID personelin kayıtlı anahtarları arasındaysa
      if (selectedPersonel.passkeys && selectedPersonel.passkeys.includes(credentialId)) {
        
        if (!assertSelectedPersonelCanCompleteLogin()) {
          setLoading(false);
          return;
        }

        // LOG KAYDI (Biyometrik olduğunu belirtiyoruz)
        await addDoc(collection(db, 'personel_hareketleri'), {
          personelId: selectedPersonel.id,
          personelIsim: `${selectedPersonel.isim} ${selectedPersonel.soyisim}`,
          islem: 'Giriş (Biyometrik)',
          sube: subeKey,
          tarih: serverTimestamp(),
          cihaz: navigator.userAgent
        });
        
        localStorage.setItem('aktifPersonel', JSON.stringify(buildSafeSessionPersonel(selectedPersonel)));
        navigate(`/dashboard?sube=${subeKey}`);
      } else {
        setError('Biyometrik doğrulama eşleşmedi.');
        setLoading(false);
      }
    } catch (err) {
      console.error("Biyometrik hata:", err);
      // Kullanıcı iptal ettiyse sessizce bekle
      setLoading(false);
    }
  };

  // Geri Dön Butonu
  const handleBack = () => {
    if (selectedPersonel) {
      // Personel seçiliyse, seçimi iptal et listeye dön
      setSelectedPersonel(null);
      setPassword('');
      setError('');
    } else {
      // Kimse seçili değilse Şube Seçimi sayfasına dön
      navigate('/subesecimi');
    }
  };

  // Yükleniyor Ekranı
  if (pageLoading) {
    return (
      <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        justifyContent: 'center',
        px: { xs: 2, sm: 0 }
      }}
    >
      
      {/* Üst Başlık ve Geri Butonu */}
      <Box display="flex" alignItems="center" mb={{ xs: 2.5, sm: 4 }}>
        <IconButton
          onClick={handleBack}
          sx={{
            mr: { xs: 1.25, sm: 2 },
            bgcolor: '#fff',
            boxShadow: 1,
            width: { xs: 40, sm: 44 },
            height: { xs: 40, sm: 44 }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {currentSubeName}
        </Typography>
      </Box>

      {/* --- DURUM A: HENÜZ PERSONEL SEÇİLMEDİ (LİSTE GÖSTER) --- */}
      {!selectedPersonel ? (
        <Paper
          elevation={3}
          sx={{
            p: { xs: 1.5, sm: 4 },
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.95)',
            width: '100%',
            maxWidth: { xs: 320, sm: 520 },
            mx: 'auto'
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            textAlign="center"
            color="textSecondary"
            sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}
          >
            Kim Giriş Yapıyor?
          </Typography>
          
          {personelList.length === 0 ? (
            <Box textAlign="center" mt={3}>
              <Typography color="error" fontWeight="bold">
                Bu şube için personel bulunamadı.
              </Typography>
              <Typography variant="body2" mt={1}>
                Yönetici panelinden personel ekleyip yetki veriniz.
              </Typography>
            </Box>
          ) : (
            <Grid
              container
              spacing={{ xs: 0.75, sm: 2 }}
              justifyContent="center"
              mt={{ xs: 0.5, sm: 1 }}
              columns={12}
            >
              {personelList.map((p) => (
                <Grid item key={p.id} xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box 
                    onClick={() => setSelectedPersonel(p)}
                    sx={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      p: { xs: 0.5, sm: 1.5 },
                      borderRadius: 2,
                      transition: '0.3s',
                      '&:hover': { bgcolor: '#e0f2fe', transform: 'scale(1.05)' }
                    }}
                  >
                    <Avatar
                      sx={{
                        width: { xs: 48, sm: 70 },
                        height: { xs: 48, sm: 70 },
                        mb: { xs: 0.6, sm: 1 },
                        bgcolor: '#000',
                        fontSize: { xs: 17, sm: 22 },
                        fontWeight: 'bold'
                      }}
                    >
                      {p.isim.charAt(0)}{p.soyisim.charAt(0)}
                    </Avatar>
                    <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, lineHeight: 1.1 }}>
                      {p.isim}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.68rem', sm: '0.75rem' } }}>
                      {p.soyisim}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      ) : (
        /* --- DURUM B: PERSONEL SEÇİLDİ (ŞİFRE SOR) --- */
        <Paper
          elevation={3}
          sx={{
            px: { xs: 1.1, sm: 3.25 },
            py: { xs: 1.7, sm: 3.25 },
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: 'rgba(255,255,255,0.95)',
            display: { xs: 'flex', sm: 'block' },
            flexDirection: { xs: 'column', sm: 'initial' },
            justifyContent: { xs: 'center', sm: 'initial' },
            width: '100%',
            maxWidth: { xs: 280, sm: 480 },
            minHeight: { xs: 300, sm: 'auto' },
            mx: 'auto'
          }}
        >
          <Avatar
            sx={{
              width: { xs: 56, sm: 80 },
              height: { xs: 56, sm: 80 },
              margin: '0 auto',
              bgcolor: '#000',
              mb: { xs: 1, sm: 2 },
              fontSize: { xs: 20, sm: 28 }
            }}
          >
            {selectedPersonel.isim.charAt(0)}{selectedPersonel.soyisim.charAt(0)}
          </Avatar>
          
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
          >
            Merhaba, {selectedPersonel.isim}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            mb={{ xs: 1.5, sm: 3 }}
            sx={{ fontSize: { xs: '0.82rem', sm: '0.95rem' } }}
          >
            Giriş yapmak için şifrenizi girin.
          </Typography>

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              autoFocus
              type="password"
              label="Şifre"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!error}
              helperText={error}
              InputProps={{
                startAdornment: <LockIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
              sx={{
                maxWidth: { xs: 248, sm: '100%' },
                mx: { xs: 'auto', sm: 0 },
                mb: { xs: 1.5, sm: 3 },
                // iOS Safari: input font-size < 16px triggers auto-zoom on focus (autoFocus here).
                '& .MuiInputBase-root': { fontSize: { xs: '16px', sm: '1rem' } },
                '& .MuiInputBase-input': { py: { xs: 0.95, sm: 1.25 } }
              }}
            />

            <Button 
              type="submit"
              variant="contained" 
              fullWidth 
              size="large"
              disabled={loading}
              sx={{ 
                maxWidth: { xs: 248, sm: '100%' },
                mx: { xs: 'auto', sm: 0 },
                bgcolor: '#000', 
                color: '#fff', 
                py: { xs: 0.9, sm: 1.5 },
                fontSize: { xs: '14px', sm: '16px' },
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#333' } 
              }}
            >
              {loading ? 'Kontrol Ediliyor...' : 'GİRİŞ YAP'}
            </Button>
          </form>

          {/* --- BİYOMETRİK GİRİŞ BUTONU (SADECE CİHAZ KAYITLIYSA GÖRÜNÜR) --- */}
          {selectedPersonel?.passkeys?.length > 0 ? (
            <Button 
              variant="outlined" 
              startIcon={<FingerprintIcon />} 
              sx={{ 
                maxWidth: { xs: 248, sm: '100%' },
                mx: { xs: 'auto', sm: 0 },
                mt: { xs: 1.5, sm: 3 }, 
                color: '#000', 
                borderColor: '#000', 
                py: { xs: 0.8, sm: 1.5 }, 
                textTransform: 'none', 
                fontWeight: 'bold',
                width: '100%',
                whiteSpace: 'nowrap',
                fontSize: { xs: '12px', sm: '14px' },
                px: { xs: 0.85, sm: 2 },
                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#333' }
              }}
              onClick={handleBiometricLogin}
              disabled={loading}
            >
              Yüz Tanıma veya Parmak İzi ile Gir
            </Button>
          ) : (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 3 }}>
              * Biyometrik giriş için personel profilinden cihaz tanımlayınız.
            </Typography>
          )}

        </Paper>
      )}

    </Container>
  );
}

export default SubeGirisi;